import React, { useCallback, useEffect, useMemo, useState } from 'react';

import usePatientToothHistory from '../procedures/widget/dental/usePatientToothHistory';
import ToothChartContainer from '../../charts/plugins/dental/ToothChartContainer';
import {
  formatRelative,
  isAutoGeneratedNote,
  tokenToHumanLabel,
} from '../procedures/widget/dental/summaryHelpers';
import useChartType from '../../charts/core/useChartType';
import useCoarsePointer from '../../hooks/useCoarsePointer';
import useTabletDentalChartStore from '../../charts/plugins/dental/tablet/useTabletDentalChartStore';

// Phase 9 — auto-open on visit-panel expand was REMOVED. The chart's
// only auto-trigger now lives in `OdontogramSection` (widget-mount).
// ToothFindingsSummary still listens for explicit `arogya:open-tooth-chart`
// events so the visit-page CTA can open the chart on demand.

/**
 * Phase 3.1.1 — ToothFindingsSummary.
 *
 * Pinned strip on the visit screen that surfaces the patient's
 * running per-tooth picture at a glance. One chip per marked token
 * (FDI / Q1–Q4 / FM). Click → opens ToothChartDialog with the
 * corresponding ToothActionsPanel auto-open.
 *
 * Cross-doctor visibility is the whole point — a referred-in doctor
 * sees Dr A's findings on day 1 without hunting through history.
 *
 * Auto-renders for any patient with tooth-tagged data; quietly absent
 * otherwise.
 *
 * Props:
 *   visit  — visit row (patient_id required)
 *   viewer — { memberId, role }
 *   isDark — boolean
 */
function _tokenLabel(token) {
  // Use the shared helper so the chip language matches the in-chart
  // summary and any future surface- or gum-scoped tokens render
  // correctly without code changes here.
  return tokenToHumanLabel(token, { compact: true });
}

export default function ToothFindingsSummary({ visit, viewer, isDark = false }) {
  const patientId = visit?.patient_id;
  const visitId = visit?.visit_id;
  const isDentist = useChartType() === 'dental';
  const {
    markers, entriesByToken, notesByToken,
  } = usePatientToothHistory(isDentist ? patientId : null);

  const [activeToken, setActiveToken] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Variant M — coarse-pointer dentists route chart-open requests
  // through `useTabletDentalChartStore`, which TabletDentalChartHost
  // mounts as `tabletPaired`. Without this redirect, dispatching
  // `arogya:open-tooth-chart` (or tapping a chip) opens a second
  // `pane`-mode chart on top of the tabletPaired one.
  const isCoarse = useCoarsePointer();
  const openTabletChart = useTabletDentalChartStore((s) => s.open);
  const tabletChartIsOpen = useTabletDentalChartStore((s) => s.isOpen);
  const requestOpen = useCallback(() => {
    if (isCoarse && isDentist) {
      openTabletChart(visit, viewer);
    } else {
      setDialogOpen(true);
    }
  }, [isCoarse, isDentist, openTabletChart, visit, viewer]);

  // F8.5 — explicit open requests (visit-page CTA, future hotkey,
  // etc.) dispatch `arogya:open-tooth-chart`. The on-mount auto-open
  // path was removed in Phase 9; the chart only opens on explicit
  // user action here.
  useEffect(() => {
    if (!visitId) return undefined;
    const onOpen = (e) => {
      if (e?.detail?.visitId && e.detail.visitId !== visitId) return;
      requestOpen();
    };
    window.addEventListener('arogya:open-tooth-chart', onOpen);
    return () => window.removeEventListener('arogya:open-tooth-chart', onOpen);
  }, [visitId, requestOpen]);

  const handleExpand = () => requestOpen();

  // Build chip rows: one per token with the most recent procedure /
  // note excerpt + author, sorted by recency.
  const rows = useMemo(() => {
    const out = [];
    const tokens = new Set([
      ...(markers ? markers.keys() : []),
      ...(entriesByToken ? entriesByToken.keys() : []),
      ...(notesByToken ? notesByToken.keys() : []),
    ]);
    tokens.forEach((token) => {
      const procs = entriesByToken?.get(token) || [];
      const notes = notesByToken?.get(token) || [];

      // Diagnoses, real notes, and auto-generated procedure prose live
      // in `notesByToken` together — split them out so the chip can
      // (a) show the most-recent HUMAN entry as the excerpt, and
      // (b) flag the "procedure started, no diagnosis yet" gap.
      const humanNotes = notes.filter((n) => !isAutoGeneratedNote(n));
      const diagnoses = humanNotes.filter((n) => (n.kind || 'note') === 'diagnosis');

      const allTimestamps = [
        ...procs.map((p) => ({
          kind: 'procedure',
          label: p.procedure_name || 'Procedure',
          author: p.completer_name || p.assignee_name,
          at: p.completed_at || p.created_at,
        })),
        ...humanNotes.map((n) => ({
          kind: 'note',
          label: (n.content || '').split('\n')[0].slice(0, 60),
          author: n.added_by_name,
          at: n.created_at,
        })),
      ].filter((e) => e.at);
      allTimestamps.sort((a, b) => (a.at < b.at ? 1 : -1));
      const mostRecent = allTimestamps[0];

      out.push({
        token,
        markerInfo: markers.get(token),
        procCount: procs.length,
        noteCount: humanNotes.length,
        diagnosisCount: diagnoses.length,
        // Surface the "treatment started but no diagnosis on record"
        // gap the dentist needs to fix. Mirrors the in-chart summary
        // PROBLEM fallback so chip + summary tell the same story.
        needsDiagnosis: procs.length > 0 && diagnoses.length === 0,
        mostRecent,
      });
    });
    // Sort chips by most-recent-first across the whole set.
    out.sort((a, b) => {
      const at = a.mostRecent?.at || '';
      const bt = b.mostRecent?.at || '';
      return at < bt ? 1 : -1;
    });
    return out;
  }, [markers, entriesByToken, notesByToken]);

  if (!patientId) return null;
  if (rows.length === 0) return null;

  const handleOpen = (token) => {
    // eslint-disable-next-line no-console
    console.info('metric=tooth_summary_chip_clicked value=1 token=%s', token);
    setActiveToken(token);
    handleExpand();
  };

  return (
    <>
      <div
        className={[
          'rounded-md border px-3 py-2 mb-3',
          isDark
            ? 'bg-slate-900 border-slate-700 text-slate-100'
            : 'bg-amber-50 border-amber-300 text-slate-900',
        ].join(' ')}
      >
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <h3 className="text-xs font-semibold flex items-center gap-1.5">
            🦷 Tooth findings
            <span className="opacity-60 font-normal">
              ({rows.length} {rows.length === 1 ? 'tooth' : 'teeth'})
            </span>
          </h3>
          <span className="text-[10px] italic opacity-70">
            click any chip to view / add
          </span>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {rows.map((row) => {
            const label = _tokenLabel(row.token);
            const recent = row.mostRecent;
            return (
              <button
                key={row.token}
                type="button"
                onClick={() => handleOpen(row.token)}
                className="inline-flex items-center gap-1.5 rounded-full border border-amber-400 dark:border-amber-700 bg-white dark:bg-slate-800 px-2 py-1 hover:bg-amber-100 dark:hover:bg-slate-700"
                style={{ fontSize: '0.6875rem' }}
                title={`${row.procCount} procedure${row.procCount === 1 ? '' : 's'} · ${row.noteCount} note${row.noteCount === 1 ? '' : 's'}${row.needsDiagnosis ? ' · no diagnosis recorded yet' : ''}`}
              >
                <strong>{label}</strong>
                {row.needsDiagnosis && (
                  <span
                    data-testid="needs-diagnosis-badge"
                    className="inline-flex items-center gap-0.5 px-1 rounded text-amber-800 dark:text-amber-200 bg-amber-200 dark:bg-amber-900/60 font-semibold"
                    title="Procedure started without a diagnosis on record. Open the chart to add one."
                    aria-label="No diagnosis recorded yet"
                  >
                    ⚠ no diagnosis
                  </span>
                )}
                {row.procCount > 0 && (
                  <span className="opacity-80">{row.procCount} proc</span>
                )}
                {row.noteCount > 0 && (
                  <span className="opacity-80">📝{row.noteCount}</span>
                )}
                {recent && (
                  <span className="opacity-60 italic max-w-[14ch] truncate">
                    {recent.kind === 'note' ? '"' : ''}
                    {recent.label}
                    {recent.kind === 'note' ? '"' : ''}
                  </span>
                )}
                {recent?.author && (
                  <span className="opacity-50">· {recent.author}</span>
                )}
                {recent?.at && (
                  <span className="opacity-50">· {formatRelative(recent.at)}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Variant M — skip the local pane-mode mount when the tablet
          chart store has already mounted a tabletPaired chart, or when
          we're on coarse-pointer + dentist (TabletDentalChartHost will
          handle it). Avoids two ToothChartContainer instances. */}
      {dialogOpen && !tabletChartIsOpen && !(isCoarse && isDentist) && (
        <ToothChartContainer
          visit={visit}
          viewer={viewer}
          initialActiveToken={activeToken}
          onClose={() => setDialogOpen(false)}
          isDark={isDark}
        />
      )}
    </>
  );
}
